import React from "react";

import { useHistory } from "react-router-dom";

import { Dialog } from "primereact";
import Lottie from "react-lottie";

import BackButtom from "../../../../components/backButton";
import FormFactory from "../../../../components/form-factory";
import ResumeFiscalNote from "../../../../components/resume";
import { Fields } from "./fields";

import { DeveloperCompanyModel } from "../../../../../domain/models/developer-company.model";

import { theme } from "../../../../styles/theme";
import { CardContent, Wrapper } from "./styles";

import * as animationData from "../../../../assets/animations/loading.json";

import { NFSeTypeEnum } from "../../../../enums/nfs-e-type.enum";
import { FiscalNoteModel } from "../../../../../domain/models/fiscal-note";
import { showErrorMessage } from "../../../../helpers/helper-toast";

type PageProps = {
  loading: boolean;
  handleSubmit: (values: any, setShowConfirm: any) => Promise<void>;
  developerCompaniesList: DeveloperCompanyModel[];
  submitting?: boolean;
};

const isSmallScreen = window.innerWidth < 768;

const IssueForm: React.FC<PageProps> = ({
  loading,
  handleSubmit,
  developerCompaniesList,
  submitting,
}) => {
  const [currentNote, setCurrentNote] = React.useState<any>();
  const [showResume, setShowResume] = React.useState<boolean>(false);
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);
  const [loadingNote, setLoadingNote] = React.useState<boolean>(false);

  const history = useHistory();
  const { state }: any = history.location;

  React.useEffect(() => {
    if (state?.copyNote) {
      setLoadingNote(true);
      const copyNote = state.copyNote as FiscalNoteModel;
      const service = copyNote.serviceList?.[0];
      setCurrentNote({
        company: Number(copyNote.developerCompany?.id),
        description: copyNote.observation,
        competencia: copyNote.competencia ? new Date(copyNote.competencia).toISOString().split("T")[0] : "",
        cnae: service?.cnae?.id,
        nationalService: service?.nationalService?.id,
        nbs: service?.nbs?.id,
        serviceValue: service?.serviceValue?.toString() || "",
        issAliquot: service?.issAliquot?.toString().replace(".", ",") || "0",
        iss: service?.issRetained ? "true" : "false",
        conditionedDiscount: service?.conditionedDiscount?.toString() || "0",
        unconditionalDiscount: service?.unconditionalDiscount?.toString() || "0",
        otherDeductions: service?.otherDeductions?.toString() || "0",
        taker: copyNote.taker,
        cnaeLabel: service?.cnae ? `${service.cnae.code} - ${service.cnae.description}` : "",
        nationalServiceLabel: service?.nationalService ? `${service.nationalService.nationalTaxCode} - ${service.nationalService.description}` : "",
        nbsLabel: service?.nbs ? `${service.nbs.codigoNbs} - ${service.nbs.description}` : "",
        // Dados de obra
        obraTipo: service?.obraTipo,
        obraCodigo: service?.obraCodigo,
        obraInscricaoImobFisc: service?.obraInscricaoImobFisc,
        obraAddress: service?.obraAddress,
      });
    }
  }, [state]);

  const findCompanyById = (companies: any[], id: string): any =>
    companies.find((item) => item.id === id)?.company;

  const getUpdatedNoteData = (
    values: any,
    selectedCompany: any,
    cnae: any,
  ): any => ({
    ...values,
    emissionDate: new Date().toISOString(),
    simplesNacional: selectedCompany?.simpleNacional,
    subscriptionNumber: selectedCompany?.subscriptionNumber || "",
    address: selectedCompany?.address || "",
    cnae,
    hasNationalFiscal: values.nfType === NFSeTypeEnum.NFS_E_NACIONAL,
  });

  const handleAddNote = async (values: any, actions: any): Promise<void> => {
    try {
      const selectedCompany = findCompanyById(
        developerCompaniesList,
        values.company,
      );

      const certificate = selectedCompany?.certificate;
      if (!certificate?.id && !certificate?.url) {
        showErrorMessage(
          "A empresa selecionada não possui um certificado digital válido. Para emitir nota fiscal, por favor, cadastre um certificado em Perfil -> Empresa.",
        );
        actions.setSubmitting(false);
        return;
      }

      const selectedCnae = values.cnae;

      const updatedNote = getUpdatedNoteData(
        values,
        selectedCompany,
        selectedCnae,
      );

      setCurrentNote((prev: any) => ({ ...prev, ...updatedNote }));
      setShowResume(true);
    } catch (error) {
      console.error("Erro ao submeter o formulário:", error);
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Wrapper>
      <BackButtom text="Emitir Nota Fiscal Avulsa" />
      <CardContent style={{ position: "relative" }}>
        {(loading || loadingNote) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.4)",
              zIndex: 9999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "8px",
            }}
          >
            <Lottie
              options={{
                loop: true,
                autoplay: true,
                animationData,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
              height={50}
              width={150}
            />
          </div>
        )}
        <FormFactory
          groups={Fields(
            developerCompaniesList,
            currentNote,
            setCurrentNote,
            setLoadingNote,
          )}
          enbaleCountGroup
          onSubmit={handleAddNote}
          enableReinitialize={loadingNote}
          backButton={{
            submit: "Voltar",
            submitting: "Voltando...",
            hide: false,
            onClick: () => history.goBack(),
            backgroundColor: theme.colors.darkBlue,
          }}
          button={{
            submit: "Gerar nota Fiscal",
            submitting: "Gerando...",
            hide: false,
            backgroundColor: theme.colors.blue400,
          }}
        />
      </CardContent>
      <Dialog
        header="Resumo da Nota Fiscal:"
        visible={showResume}
        style={{
          width: window.innerWidth < 479 ? "90vw" : "50vw",
          backgroundColor: theme.colors.darkBlue,
        }}
        headerStyle={{
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
        }}
        contentStyle={{
          backgroundColor: theme.colors.darkBlue,
        }}
        footer={null}
        onHide={() => setShowResume(false)}
      >
        <ResumeFiscalNote
          submitting={submitting}
          fiscalNote={currentNote}
          onConfirmCallback={async () => {
            await handleSubmit(currentNote, setShowConfirm);
          }}
          onCancelCallback={() => setShowResume(false)}
        />
      </Dialog>
      <Dialog
        header="Aguarde!"
        visible={showConfirm}
        headerStyle={{
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
          fontFamily: theme.font.poppins,
        }}
        style={{
          width: isSmallScreen ? "90vw" : "35vw",
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
          fontFamily: theme.font.poppins,
        }}
        contentStyle={{
          backgroundColor: theme.colors.darkBlue,
          color: theme.colors.white,
          fontFamily: theme.font.poppins,
        }}
        footer={null}
        onHide={() => null}
        closable={false}
      >
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData,
            rendererSettings: {
              preserveAspectRatio: "xMidYMid slice",
            },
          }}
          height={20}
          width={100}
          isStopped={false}
          isPaused={false}
        />
        <p className="text-center mt-4" style={{ maxWidth: "100%" }}>
          Nota fiscal sendo gerada. Você será redirecionado para tela de
          Listagem onde poderá fazer o download do documento quando estiver
          disponivel.
        </p>
      </Dialog>
    </Wrapper>
  );
};
export default IssueForm;
